/**
 * Store Memory Service
 * API service for managing store memory notes
 */

import { api } from './api'
import {
  StoreMemory,
  StoreMemoryWithCreator,
  CreateStoreMemoryRequest,
  UpdateStoreMemoryRequest,
  StoreMemoryCategory,
  MAX_STORE_NOTES
} from '@shared/types'

interface ListMemoriesResponse {
  success: boolean
  data: {
    memories: StoreMemoryWithCreator[]
    count: number
    storeId: string
  }
}

interface SingleMemoryResponse {
  success: boolean
  data: StoreMemoryWithCreator
  message?: string
}

interface CountResponse {
  success: boolean
  data: {
    storeId: string
    activeCount: number
    maxNotes: number
  }
}

interface CategoriesResponse {
  success: boolean
  data: StoreMemoryCategory[]
}

interface ListAllResponse {
  success: boolean
  data: {
    memories: StoreMemoryWithCreator[]
    total: number
    hasMore: boolean
  }
}

export const storeMemoryService = {
  /**
   * List memories for a specific store
   */
  list: async (storeId: string, options?: {
    category?: string
    includeInactive?: boolean
  }): Promise<StoreMemoryWithCreator[]> => {
    const params = new URLSearchParams()
    if (options?.category) params.append('category', options.category)
    if (options?.includeInactive) params.append('includeInactive', 'true')

    const queryString = params.toString()
    const url = `/store-memory/${storeId}${queryString ? `?${queryString}` : ''}`

    const response = await api.get<ListMemoriesResponse>(url)
    return response.data.memories
  },

  /**
   * Get a single memory by ID
   */
  getById: async (id: string): Promise<StoreMemoryWithCreator> => {
    const response = await api.get<SingleMemoryResponse>(`/store-memory/note/${id}`)
    return response.data
  },

  /**
   * Create a new memory note
   */
  create: async (data: CreateStoreMemoryRequest): Promise<StoreMemoryWithCreator> => {
    const response = await api.post<SingleMemoryResponse>('/store-memory', data)
    return response.data
  },

  /**
   * Update a memory note
   */
  update: async (id: string, data: UpdateStoreMemoryRequest): Promise<StoreMemoryWithCreator> => {
    const response = await api.put<SingleMemoryResponse>(`/store-memory/${id}`, data)
    return response.data
  },

  /**
   * Delete a memory note
   */
  delete: async (id: string): Promise<void> => {
    await api.delete(`/store-memory/${id}`)
  },

  /**
   * Get active note count for a store
   */
  getCount: async (storeId: string): Promise<{ activeCount: number; maxNotes: number }> => {
    const response = await api.get<CountResponse>(`/store-memory/${storeId}/count`)
    return {
      activeCount: response.data.activeCount,
      maxNotes: response.data.maxNotes
    }
  },

  // Admin-only methods

  /**
   * List all memories across all stores (admin only)
   */
  listAll: async (options?: {
    storeId?: string
    category?: string
    includeInactive?: boolean
    limit?: number
    offset?: number
  }): Promise<{ memories: StoreMemoryWithCreator[]; total: number; hasMore: boolean }> => {
    const params = new URLSearchParams()
    if (options?.storeId) params.append('storeId', options.storeId)
    if (options?.category) params.append('category', options.category)
    if (options?.includeInactive) params.append('includeInactive', 'true')
    if (options?.limit) params.append('limit', options.limit.toString())
    if (options?.offset) params.append('offset', options.offset.toString())

    const queryString = params.toString()
    const url = `/store-memory/admin/all${queryString ? `?${queryString}` : ''}`

    const response = await api.get<ListAllResponse>(url)
    return response.data
  },

  /**
   * Get all categories (admin only)
   */
  getCategories: async (): Promise<StoreMemoryCategory[]> => {
    const response = await api.get<CategoriesResponse>('/store-memory/admin/categories')
    return response.data
  }
}

export { MAX_STORE_NOTES }
